import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../../../../shared/components/LoadingSpinner';
import userService, { Role } from '../../../../shared/services/userService';
import RoleDefaultAccessModal from './modals/RoleDefaultAccessModal';
import { useRoleDefaultAccessManagement } from '../hooks/useRoleDefaultAccessManagement';

/**
 * Contenu « Accès par défaut » réutilisable (sans layout).
 * Utilisé dans RoleDefaultAccessManagement et dans CmManagement (onglet Référent CM).
 */
const RoleDefaultAccessContent: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const roleDefaultAccessManagement = useRoleDefaultAccessManagement();

  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await userService.getRoles();
        if (response.success) {
          setRoles(response.data);
        } else {
          setError('Erreur lors du chargement des rôles');
        }
      } catch (err: any) {
        setError(err.message || 'Erreur lors du chargement des rôles');
      } finally {
        setLoading(false);
      }
    };

    loadRoles();
  }, []);

  const handleOpenModal = async (roleId: number) => {
    try {
      const success = await roleDefaultAccessManagement.loadRoleDefaultAccess(roleId);
      if (success) {
        setSelectedRoleId(roleId);
        setShowModal(true);
      } else {
        setError('Erreur lors du chargement des accès par défaut');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des accès par défaut');
    }
  };

  const handleToggleAccess = async (section: string, hasAccess: boolean) => {
    if (!selectedRoleId) return;

    try {
      await roleDefaultAccessManagement.toggleAccess(
        selectedRoleId,
        section,
        hasAccess
      );
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour de l\'accès par défaut');
    }
  };

  const selectedRole = roles.find(r => r.id === selectedRoleId) || null;

  return (
    <>
      {error && (
        <div className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-6 backdrop-blur-sm">
          <h2 className="text-base font-bold text-white mb-2">
            Sélectionner un rôle
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            Choisissez un rôle pour gérer ses accès par défaut aux différentes sections du panel.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => handleOpenModal(role.id)}
                className="p-4 rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 hover:border-[#3d4fdb]/60 hover:bg-[#3d4fdb]/10 transition-all text-left"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-white">
                    {role.role_name}
                  </h3>
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                {role.description && (
                  <p className="text-xs text-slate-400">
                    {role.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {showModal && selectedRoleId && (
        <RoleDefaultAccessModal
          role={selectedRole}
          roleDefaultAccess={roleDefaultAccessManagement.roleDefaultAccess}
          panelSections={roleDefaultAccessManagement.panelSections}
          updating={roleDefaultAccessManagement.updating}
          getSectionAccess={roleDefaultAccessManagement.getSectionAccess}
          onToggleAccess={handleToggleAccess}
          onClose={() => {
            setShowModal(false);
            setSelectedRoleId(null);
            roleDefaultAccessManagement.reset();
          }}
        />
      )}
    </>
  );
};

export default RoleDefaultAccessContent;
